import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import Footer from "../components/Footer";
import Seo from "../components/Seo";

/**
 * NotFound — branded 404 catch-all. Caught by App.js's Routes; visually
 * matches the rest of the marketing site so users hitting a broken link
 * (typo in URL, expired order, old social share) get a credible page
 * with a clear next step, not a stark default browser error.
 */
export default function NotFound() {
  return (
    <main data-testid="not-found-page" className="bg-black text-zinc-100 min-h-screen flex flex-col">
      <Seo
        title="Page not found"
        description="The page you're looking for doesn't exist on crypt.pe."
        noindex
      />
      <div className="flex-1 flex items-center justify-center px-6 py-20">
        <div className="max-w-md w-full text-center">
          <div
            aria-hidden
            className="font-display select-none text-[7rem] sm:text-[9rem] font-black leading-none tracking-tighter text-transparent"
            style={{ WebkitTextStroke: "1.5px rgba(255,255,255,0.22)" }}
          >
            404
          </div>
          <div className="mono mt-2 text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-600">page not found</div>
          <h1 className="mt-5 text-3xl sm:text-4xl font-black lowercase tracking-tight">
            that link doesn&apos;t lead anywhere.
          </h1>
          <p className="mt-4 text-[14px] text-zinc-500 leading-relaxed">
            The page may have moved, the order may have expired, or the link may be a typo.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              data-testid="404-home-link"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white hover:bg-neutral-200 text-black rounded-lg h-12 px-7 text-sm font-bold lowercase transition-colors"
            >
              back to home <ArrowRight size={14} />
            </Link>
            <Link
              to="/signup"
              data-testid="404-signup-link"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-white/15 bg-white/[0.03] hover:bg-white/[0.07] text-zinc-200 rounded-lg h-12 px-7 text-sm font-medium lowercase transition-colors"
            >
              claim your page →
            </Link>
          </div>
          <div className="mt-10 pt-7 border-t border-white/10">
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-4 inline-flex items-center gap-1.5">
              <Search size={10} /> maybe you were looking for
            </div>
            <div className="flex flex-wrap justify-center gap-2 [&>a]:grow sm:[&>a]:grow-0 [&>a]:text-center">
              {[
                ["/pricing", "pricing"],
                ["/faq", "faq"],
                ["/docs", "api docs"],
                ["/guides", "guides"],
                ["/payment-help", "payment help"],
                ["/compare", "compare gateways"],
              ].map(([to, label]) => (
                <Link
                  key={to}
                  to={to}
                  data-testid={`404-link-${label.replace(/\s+/g, "-")}`}
                  className="font-mono text-[11px] border border-zinc-700 bg-white/[0.04] hover:border-zinc-500 hover:bg-white/[0.08] rounded-full px-3.5 py-2 text-zinc-300 hover:text-white transition-colors lowercase"
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}
